/**
 * Adjacency Detector Component
 *
 * Automatically detects adjacent surfaces between zones and provides
 * a UI for reviewing and linking them.
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useGeometryEditStore, useAdjacencyDetector } from '../../geometryEditStore';
import { useGeometryStore } from '../../geometryStore';
import { useIDFStore } from '../../../stores/idfStore';
import type { AdjacencyResult, MatchQuality } from '../../editingTypes';
import { linkAdjacentSurfaces, unlinkAdjacentSurfaces } from '../../sync/surfaceSync';

/**
 * Props for AdjacencyDetector.
 */
interface AdjacencyDetectorProps {
  onClose?: () => void;
}

/**
 * Get color for match quality badge.
 */
function getMatchQualityColor(quality: MatchQuality): string {
  switch (quality) {
    case 'exact':
      return 'bg-green-100 text-green-800';
    case 'close':
      return 'bg-yellow-100 text-yellow-800';
    case 'partial':
      return 'bg-orange-100 text-orange-800';
  }
}

/**
 * Adjacency Detector Component.
 */
export function AdjacencyDetector({ onClose }: AdjacencyDetectorProps) {
  const detector = useAdjacencyDetector();
  const geometry = useGeometryStore((s) => s.geometry);
  const setSelectedIds = useGeometryStore((s) => s.setSelectedIds);

  const detectAdjacencies = useGeometryEditStore((s) => s.detectAdjacencies);
  const setReviewingAdjacency = useGeometryEditStore((s) => s.setReviewingAdjacency);
  const linkAdjacentSurfacesAction = useGeometryEditStore((s) => s.linkAdjacentSurfaces);
  const unlinkAdjacentSurfacesAction = useGeometryEditStore((s) => s.unlinkAdjacentSurfaces);
  const setAdjacencySettings = useGeometryEditStore((s) => s.setAdjacencySettings);
  const addNotification = useGeometryEditStore((s) => s.addNotification);

  const updateObject = useIDFStore((s) => s.updateObject);
  const getObjectById = useIDFStore((s) => s.getObjectById);

  // Highlight surfaces in 3D view when reviewing an adjacency
  useEffect(() => {
    if (detector.reviewingIndex !== null && detector.results[detector.reviewingIndex]) {
      const result = detector.results[detector.reviewingIndex];
      if (result) {
        setSelectedIds([result.surface1Id, result.surface2Id]);
      }
    }
  }, [detector.reviewingIndex, detector.results, setSelectedIds]);

  // Stats
  const stats = useMemo(() => {
    const results = detector.results;
    return {
      total: results.length,
      linked: results.filter((r) => r.isLinked).length,
      unlinked: results.filter((r) => !r.isLinked).length,
      exact: results.filter((r) => r.matchQuality === 'exact').length,
      close: results.filter((r) => r.matchQuality === 'close').length,
      partial: results.filter((r) => r.matchQuality === 'partial').length,
    };
  }, [detector.results]);

  const handleDetect = useCallback(() => {
    if (!geometry) {
      addNotification('error', 'No geometry loaded');
      return;
    }

    detectAdjacencies(geometry.zones);
  }, [geometry, detectAdjacencies, addNotification]);

  const handleLink = useCallback(
    (result: AdjacencyResult) => {
      const surface1 = getObjectById(result.surface1Id);
      const surface2 = getObjectById(result.surface2Id);

      if (!surface1 || !surface2) {
        addNotification('error', 'Could not find surfaces');
        return;
      }

      // Update IDF objects
      const { surface1: updated1, surface2: updated2 } = linkAdjacentSurfaces(surface1, surface2);

      updateObject(surface1.id, { fields: updated1.fields });
      updateObject(surface2.id, { fields: updated2.fields });

      // Update store
      linkAdjacentSurfacesAction(result.surface1Id, result.surface2Id);
    },
    [getObjectById, updateObject, linkAdjacentSurfacesAction, addNotification]
  );

  const handleUnlink = useCallback(
    (result: AdjacencyResult) => {
      const surface1 = getObjectById(result.surface1Id);
      const surface2 = getObjectById(result.surface2Id);

      if (!surface1 || !surface2) {
        addNotification('error', 'Could not find surfaces');
        return;
      }

      // Update IDF objects
      const { surface1: updated1, surface2: updated2 } = unlinkAdjacentSurfaces(surface1, surface2);

      updateObject(surface1.id, { fields: updated1.fields });
      updateObject(surface2.id, { fields: updated2.fields });

      // Update store
      unlinkAdjacentSurfacesAction(result.surface1Id, result.surface2Id);
    },
    [getObjectById, updateObject, unlinkAdjacentSurfacesAction, addNotification]
  );

  const handleLinkAll = useCallback(() => {
    const unlinked = detector.results.filter((r) => !r.isLinked && r.matchQuality !== 'partial');
    let linked = 0;

    for (const result of unlinked) {
      try {
        handleLink(result);
        linked++;
      } catch {
        // Continue with next
      }
    }

    addNotification('success', `Linked ${linked} surface pairs`);
  }, [detector.results, handleLink, addNotification]);

  return (
    <div className="flex h-full flex-col bg-card">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="font-medium text-foreground">Surface Adjacency Detection</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded"
            aria-label="Close adjacency detector"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Settings */}
      <div className="border-b border-border bg-muted/50 px-4 py-3">
        <div className="mb-2 text-sm font-medium text-foreground">Detection Settings</div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <label htmlFor="max-distance" className="mb-1 block text-xs text-muted-foreground">Max Distance (m)</label>
            <input
              type="number"
              id="max-distance"
              value={detector.settings.maxDistance}
              onChange={(e) =>
                setAdjacencySettings({ maxDistance: parseFloat(e.target.value) || 0.1 })
              }
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              step="0.01"
              min="0.01"
            />
          </div>
          <div>
            <label htmlFor="min-overlap" className="mb-1 block text-xs text-muted-foreground">Min Overlap Area (m²)</label>
            <input
              type="number"
              id="min-overlap"
              value={detector.settings.minOverlapArea}
              onChange={(e) =>
                setAdjacencySettings({ minOverlapArea: parseFloat(e.target.value) || 0.1 })
              }
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              step="0.1"
              min="0.1"
            />
          </div>
        </div>
        <button
          onClick={handleDetect}
          className="mt-3 w-full rounded bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background"
        >
          {detector.hasRun ? 'Re-detect Adjacencies' : 'Detect Adjacencies'}
        </button>
      </div>

      {/* Results */}
      {detector.hasRun && (
        <>
          {/* Stats */}
          <div className="border-b border-border bg-card px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <span className="font-medium text-foreground">{stats.total}</span>
                <span className="text-muted-foreground"> potential adjacencies</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded bg-green-100 px-2 py-0.5 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                  {stats.linked} linked
                </span>
                <span className="rounded bg-muted px-2 py-0.5 text-muted-foreground">
                  {stats.unlinked} unlinked
                </span>
              </div>
            </div>
            {stats.unlinked > 0 && (
              <button
                onClick={handleLinkAll}
                className="mt-2 w-full rounded bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-background"
              >
                Link All Exact & Close Matches
              </button>
            )}
          </div>

          {/* Results list */}
          <div className="flex-1 overflow-auto">
            {detector.results.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No adjacencies detected. Try adjusting the settings.
              </div>
            ) : (
              <div className="divide-y divide-border">
                {detector.results.map((result, index) => (
                  <div
                    key={`${result.surface1Id}-${result.surface2Id}`}
                    className={`cursor-pointer px-4 py-3 transition-colors ${
                      detector.reviewingIndex === index
                        ? 'bg-primary/5 ring-1 ring-inset ring-primary/20'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setReviewingAdjacency(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setReviewingAdjacency(index)}
                    aria-selected={detector.reviewingIndex === index}
                    aria-label={`Adjacency between ${result.surface1Name} and ${result.surface2Name}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded px-1.5 py-0.5 text-xs font-medium ${getMatchQualityColor(
                              result.matchQuality
                            )}`}
                          >
                            {result.matchQuality}
                          </span>
                          {result.isLinked && (
                            <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                              linked
                            </span>
                          )}
                          {detector.reviewingIndex === index && (
                            <span className="flex items-center gap-1 text-xs text-primary">
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              viewing in 3D
                            </span>
                          )}
                        </div>
                        <div className="mt-1 text-sm">
                          <div className="font-medium text-foreground">
                            {result.surface1Name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {result.surface1Zone}
                          </div>
                        </div>
                        <div className="mt-1 flex items-center text-xs text-muted-foreground/70">
                          <svg className="mr-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                          </svg>
                          adjacent to
                        </div>
                        <div className="mt-1 text-sm">
                          <div className="font-medium text-foreground">
                            {result.surface2Name}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {result.surface2Zone}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4">
                        {result.isLinked ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUnlink(result);
                            }}
                            className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
                            aria-label={`Unlink ${result.surface1Name} from ${result.surface2Name}`}
                          >
                            Unlink
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleLink(result);
                            }}
                            className="rounded bg-green-100 px-2 py-1 text-xs text-green-700 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                            aria-label={`Link ${result.surface1Name} to ${result.surface2Name}`}
                          >
                            Link
                          </button>
                        )}
                      </div>
                    </div>
                    {detector.reviewingIndex === index && (
                      <div className="mt-3 grid grid-cols-3 gap-2 rounded bg-muted/50 p-2 text-xs">
                        <div>
                          <div className="text-muted-foreground">Overlap</div>
                          <div className="font-medium text-foreground">{result.overlapArea.toFixed(2)} m²</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Normal Align</div>
                          <div className="font-medium text-foreground">{(result.normalAlignment * 100).toFixed(0)}%</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Distance</div>
                          <div className="font-medium text-foreground">{result.centerDistance.toFixed(3)} m</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border bg-muted/50 px-4 py-3 text-xs text-muted-foreground">
            Click on a result to view the surfaces highlighted in 3D. Linked surfaces share heat transfer.
          </div>
        </>
      )}

      {!detector.hasRun && (
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
          <svg className="mb-4 h-16 w-16 text-muted-foreground/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <p className="text-sm font-medium text-foreground">No Detection Run Yet</p>
          <p className="mt-1 max-w-xs text-sm text-muted-foreground">
            Click "Detect Adjacencies" to find surfaces that share boundaries between zones.
          </p>
        </div>
      )}
    </div>
  );
}

export default AdjacencyDetector;
